import formatDate from 'date-fns/format';
import { Box, Button, MenuItem, TextField, Theme, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfFormRow, colorPalette } from '@qualifio/ui-commons';
import { faCalendar } from '@fortawesome/pro-solid-svg-icons/faCalendar';
import { faEye } from '@fortawesome/pro-solid-svg-icons/faEye';
import { faShareNodes } from '@fortawesome/pro-solid-svg-icons/faShareNodes';
import { makeStyles } from '@mui/styles';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TemplateVersionMenuButton, TemplateVersionMenuManager } from './TemplateVersionMenu';
import { useEditorStore } from '../../store';
import { usePortalUrl } from '../../../shared/open-portal-with-internal-auth';
import { useProgram } from '../../../loyalty/ProgramContext';
import {
  usePublishedTemplate,
  useScheduledTemplate,
  useTemplates,
} from '../../hooks/use-templates';

const useStyles = makeStyles<Theme>(() => ({
  versionIcon: {
    color: colorPalette.green.main,
    width: '16px',
    paddingRight: 10,
  },
  scheduledIcon: {
    color: colorPalette.orange.main,
    width: '16px',
    paddingRight: 10,
  },
  menuButton: {
    padding: '4px',
    minWidth: 'auto',
    width: '24px',
    height: '24px',
    '& svg': {
      fontSize: '14px',
    },
  },
  scheduledText: {
    color: colorPalette.orange.main,
    fontSize: '11px',
  },
}));

export function TemplateVersionsDropdown() {
  const { t } = useTranslation();
  const { program } = useProgram();
  const { openPortal } = usePortalUrl(program.id);
  const classes = useStyles();

  // Use Zustand store
  const currentTemplateId = useEditorStore(state => state.currentTemplateId);
  const setCurrentTemplateId = useEditorStore(state => state.setCurrentTemplateId);

  const timeDateFormat = t('timeDateFormat') ?? 'dd/MM/yyyy HH:mm:ss';
  const [isOpen, setIsOpen] = useState(false);
  const keepOpenRef = useRef(false);
  const scrollableElementRef = useRef<HTMLElement | null>(null);

  const { templates, hasNextPage, isFetchingNextPage, fetchNextPage } = useTemplates();
  const { data: publishedTemplate } = usePublishedTemplate();
  const { data: scheduledTemplate } = useScheduledTemplate();

  const handleScroll = useCallback(
    (event: Event) => {
      const target = event.target as HTMLElement;
      if (!target) return;
      const { scrollTop, scrollHeight, clientHeight } = target;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // Load when 100px from bottom
      if (isNearBottom && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  useEffect(() => {
    if (isOpen) {
      const timeOutId = setTimeout(() => {
        const scrollableElement = (document.querySelector('[role="listbox"]') as HTMLElement)
          ?.parentElement;
        if (scrollableElement) {
          scrollableElementRef.current = scrollableElement;
          scrollableElement.addEventListener('scroll', handleScroll, { passive: true });
        }
      });
      return () => {
        clearTimeout(timeOutId);
        if (scrollableElementRef.current) {
          scrollableElementRef.current.removeEventListener('scroll', handleScroll);
          scrollableElementRef.current = null;
        }
      };
    } else {
      // Clean up when menu closes
      if (scrollableElementRef.current) {
        scrollableElementRef.current.removeEventListener('scroll', handleScroll);
        scrollableElementRef.current = null;
      }
    }
  }, [isOpen, handleScroll]);

  return (
    <TemplateVersionMenuManager>
      <QlfFormRow
        label={t('editor.version.select.previous')}
        sxs={{
          label: { width: 'auto' },
          row: { marginBottom: 0 },
        }}
      >
        <TextField
          select
          id="template-version-selector"
          value={currentTemplateId || ''}
          sx={{ p: 1 }}
          InputProps={{ sx: { height: '50px', width: '300px', p: 0 } }}
          SelectProps={{
            open: isOpen,
            onOpen: () => setIsOpen(true),
            onClose: () => {
              // Ignore the close triggered right after a selection
              if (keepOpenRef.current) {
                keepOpenRef.current = false;
                setIsOpen(true);
                return;
              }
              setIsOpen(false);
            },
            MenuProps: {
              sx: { maxHeight: '40vh' },
            },
          }}
          onChange={e => {
            keepOpenRef.current = true;
            setCurrentTemplateId(e.target.value);
          }}
        >
          {[
            ...(publishedTemplate ? [publishedTemplate] : []),
            ...(scheduledTemplate ? [scheduledTemplate] : []),
            ...templates.filter(
              t => t.id !== publishedTemplate?.id && t.id !== scheduledTemplate?.id,
            ),
          ].map(templateVersion => {
            const isPublished = templateVersion.id === publishedTemplate?.id;
            const isScheduled = !!templateVersion.scheduledPublicationDate;
            return (
              <MenuItem
                value={templateVersion.id}
                key={templateVersion.id}
                sx={{
                  paddingRight: 1,
                  paddingLeft: 1,
                  '&:hover .menuItemTemplateActions': { opacity: 1 },
                  '& svg': { margin: 0 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  {isPublished && (
                    <Tooltip title={t('editor.template.version.published')} placement="top">
                      <FontAwesomeIcon icon={faShareNodes} className={classes.versionIcon} />
                    </Tooltip>
                  )}
                  {isScheduled && (
                    <Tooltip title={t('editor.template.version.scheduled')} placement="top">
                      <FontAwesomeIcon icon={faCalendar} className={classes.scheduledIcon} />
                    </Tooltip>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    {templateVersion.name && (
                      <Typography
                        fontSize={14}
                        fontWeight={600}
                        title={templateVersion.name}
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '22ch',
                        }}
                      >
                        {templateVersion.name}
                      </Typography>
                    )}
                    {(!templateVersion.name || !isScheduled) && (
                      <Typography fontSize={!templateVersion.name ? 14 : 12} color="text.secondary">
                        {formatDate(new Date(templateVersion.createdAt!), timeDateFormat)}
                      </Typography>
                    )}
                    {isScheduled && templateVersion.scheduledPublicationDate && (
                      <Typography className={classes.scheduledText}>
                        {t('editor.template.version.scheduled.label', {
                          date: formatDate(
                            new Date(templateVersion.scheduledPublicationDate),
                            timeDateFormat,
                          ),
                        })}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      marginLeft: 1,
                      opacity: 0,
                      transition: 'opacity 0.3s',
                    }}
                    className="menuItemTemplateActions"
                  >
                    <Tooltip title={t('editor.template.version.see')} placement="top">
                      <Button
                        sx={{
                          backgroundColor: `${colorPalette.blue.main}`,
                          width: '30px',
                          height: '30px',
                          '& svg': {
                            opacity: 0.5,
                          },
                          '&:hover': {
                            backgroundColor: `${colorPalette.blue.main}`,
                            '& svg': { opacity: 1 },
                          },
                        }}
                        onClick={event => {
                          event.stopPropagation();
                          openPortal({ templateId: templateVersion.id, withInterAuth: true });
                        }}
                      >
                        <FontAwesomeIcon icon={faEye} className={classes.seeVersionIcon} />
                      </Button>
                    </Tooltip>
                    <TemplateVersionMenuButton templateId={templateVersion.id!} />
                  </Box>
                </Box>
              </MenuItem>
            );
          })}
          {isFetchingNextPage && (
            <MenuItem disabled>
              <div style={{ padding: '8px', textAlign: 'center', width: '100%' }}>
                {t('generic.loading')}
              </div>
            </MenuItem>
          )}
        </TextField>
      </QlfFormRow>
    </TemplateVersionMenuManager>
  );
}
